
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { OutletApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import {
  Shield,
  AlertTriangle,
  TrendingUp,
  Users,
  DollarSign,
  Activity,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Eye,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Zap
} from "lucide-react";

interface FraudAlert {
  id: string;
  memberId: string;
  memberName: string;
  alertType: 'velocity' | 'pattern' | 'threshold' | 'blockchain';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  timestamp: string;
  status: 'active' | 'investigating' | 'resolved' | 'false_positive';
  riskScore: number;
  transactionId?: string;
  blockchainTxId?: string;
  metadata: {
    redemptionAmount?: number;
    velocityScore?: number;
    patternAnomaly?: string;
    walletHops?: number;
    gasPrice?: string;
  };
}

interface DetectionModel {
  id: string;
  name: string;
  type: 'velocity' | 'pattern' | 'threshold' | 'blockchain';
  status: 'active' | 'paused' | 'training';
  accuracy: number;
  lastTrained: string;
  alertsGenerated: number;
  description: string;
}

interface OutletOption {
  id: string;
  name: string;
}

const fraudRuleFormSchema = z.object({
  ruleName: z.string().min(1, "Rule name is required"),
  outletId: z.string().min(1, "Outlet is required"),
  transactionCount: z.string().min(1, "Number of transactions is required"),
  days: z.string().min(1, "Days is required"),
  invoiceAmountThreshold: z
    .string()
    .min(1, "Invoice amount is required"),
  invoiceAction: z.string().min(1, "Action is required"),
  redeemAmountThreshold: z
    .string()
    .min(1, "Redemption amount is required"),
  redeemAction: z.string().min(1, "Action is required"),
});

type FraudRuleFormValues = z.infer<typeof fraudRuleFormSchema>;

const FraudDetection = () => {
  const { toast } = useToast();
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(75);
  const [autoBlockEnabled, setAutoBlockEnabled] = useState(false);
  const [autoBlockThreshold, setAutoBlockThreshold] = useState(90);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [outlets, setOutlets] = useState<OutletOption[]>([]);

  // Sample fraud alerts
  const [fraudAlerts] = useState<FraudAlert[]>([
    {
      id: 'FA-001',
      memberId: 'MEM-789',
      memberName: 'John Suspicious',
      alertType: 'velocity',
      severity: 'high',
      description: 'Unusual redemption velocity: 15 transactions in 10 minutes',
      timestamp: '2025-05-29 14:32:15',
      status: 'active',
      riskScore: 85,
      transactionId: 'TX-2345-15',
      blockchainTxId: '0x7a58c0340a8b42c323edgfe852f651e047fbfa7a2a52689fe715a',
      metadata: {
        redemptionAmount: 5000,
        velocityScore: 85,
        walletHops: 3
      }
    },
    {
      id: 'FA-002',
      memberId: 'MEM-456',
      memberName: 'Sarah Pattern',
      alertType: 'pattern',
      severity: 'medium',
      description: 'Anomalous redemption pattern detected - unusual time and location',
      timestamp: '2025-05-29 13:15:42',
      status: 'investigating',
      riskScore: 68,
      transactionId: 'TX-2345-12',
      metadata: {
        redemptionAmount: 1200,
        patternAnomaly: 'Location + Time mismatch',
        velocityScore: 45
      }
    },
    {
      id: 'FA-003',
      memberId: 'MEM-123',
      memberName: 'Mike Threshold',
      alertType: 'threshold',
      severity: 'critical',
      description: 'Single redemption exceeds daily limit by 300%',
      timestamp: '2025-05-29 12:08:33',
      status: 'resolved',
      riskScore: 95,
      transactionId: 'TX-2345-08',
      metadata: {
        redemptionAmount: 15000,
        velocityScore: 25
      }
    },
    {
      id: 'FA-004',
      memberId: 'MEM-321',
      memberName: 'Alice Hopper',
      alertType: 'blockchain',
      severity: 'high',
      description: 'Multiple wallet hops detected before redemption',
      timestamp: '2025-05-29 11:45:18',
      status: 'false_positive',
      riskScore: 78,
      blockchainTxId: '0x9b67d430ab5c23edcc835629db035c7f9235ffa35664be3d1d6f617',
      metadata: {
        walletHops: 7,
        gasPrice: '45 gwei',
        redemptionAmount: 2500
      }
    },
    {
      id: 'FA-005',
      memberId: 'MEM-555',
      memberName: 'David Rapid',
      alertType: 'velocity',
      severity: 'medium',
      description: 'High transaction frequency: 8 redemptions in 5 minutes',
      timestamp: '2025-05-29 10:30:22',
      status: 'active',
      riskScore: 72,
      transactionId: 'TX-2345-20',
      metadata: {
        redemptionAmount: 3200,
        velocityScore: 72,
      }
    },
    {
      id: 'FA-006',
      memberId: 'MEM-666',
      memberName: 'Emma Large',
      alertType: 'threshold',
      severity: 'high',
      description: 'Redemption amount exceeds weekly limit by 250%',
      timestamp: '2025-05-29 09:15:10',
      status: 'investigating',
      riskScore: 88,
      transactionId: 'TX-2345-18',
      metadata: {
        redemptionAmount: 18000,
        velocityScore: 35
      }
    },
    {
      id: 'FA-007',
      memberId: 'MEM-777',
      memberName: 'Robert Pattern',
      alertType: 'pattern',
      severity: 'medium',
      description: 'Unusual redemption pattern - multiple locations in short time',
      timestamp: '2025-05-29 08:45:55',
      status: 'active',
      riskScore: 65,
      transactionId: 'TX-2345-16',
      metadata: {
        redemptionAmount: 2800,
        patternAnomaly: 'Multiple locations',
        velocityScore: 58
      }
    },
    {
      id: 'FA-008',
      memberId: 'MEM-888',
      memberName: 'Lisa Suspicious',
      alertType: 'velocity',
      severity: 'critical',
      description: 'Extreme velocity detected: 20 transactions in 15 minutes',
      timestamp: '2025-05-29 07:20:33',
      status: 'resolved',
      riskScore: 98,
      transactionId: 'TX-2345-14',
      metadata: {
        redemptionAmount: 8500,
        velocityScore: 98,
      }
    }
  ]);

  // Sample detection models
  const [detectionModels] = useState<DetectionModel[]>([
    {
      id: 'MODEL-001',
      name: 'Velocity Anomaly Detector',
      type: 'velocity',
      status: 'active',
      accuracy: 92.5,
      lastTrained: '2025-05-28',
      alertsGenerated: 156,
      description: 'Detects unusual transaction velocity patterns'
    },
    {
      id: 'MODEL-002',
      name: 'Pattern Recognition Engine',
      type: 'pattern',
      status: 'active',
      accuracy: 88.3,
      lastTrained: '2025-05-27',
      alertsGenerated: 89,
      description: 'Identifies anomalous behavioral patterns'
    },
    {
      id: 'MODEL-003',
      name: 'Threshold Monitor',
      type: 'threshold',
      status: 'active',
      accuracy: 95.8,
      lastTrained: '2025-05-26',
      alertsGenerated: 34,
      description: 'Monitors redemption amounts against dynamic thresholds'
    },
    {
      id: 'MODEL-004',
      name: 'Blockchain Analyzer',
      type: 'blockchain',
      status: 'training',
      accuracy: 86.7,
      lastTrained: '2025-05-25',
      alertsGenerated: 67,
      description: 'Analyzes on-chain behavior and wallet patterns'
    }
  ]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 hover:bg-red-100';
      case 'high': return 'bg-orange-100 text-orange-800 hover:bg-orange-100';
      case 'medium': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'low': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800 hover:bg-red-100';
      case 'investigating': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'resolved': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'false_positive': return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const getModelStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'paused': return <Pause className="h-4 w-4 text-yellow-600" />;
      case 'training': return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      default: return <XCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const handleAlertAction = (alertId: string, action: string) => {
    toast({
      title: `Alert ${action}`,
      description: `Alert ${alertId} has been ${action.toLowerCase()}.`,
    });
  };

  const handleModelToggle = (modelId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    toast({
      title: `Model ${newStatus}`,
      description: `Detection model has been ${newStatus}.`,
    });
  };

  const activeAlerts = fraudAlerts.filter(alert => alert.status === 'active').length;
  const highRiskAlerts = fraudAlerts.filter(alert => alert.severity === 'high' || alert.severity === 'critical').length;

  // Fetch outlets from API
  useEffect(() => {
    if (isAddModalOpen) {
      fetchOutlets();
    }
  }, [isAddModalOpen]);

  const fetchOutlets = async () => {
    try {
      const response = await OutletApi.getAllOutlets();
      
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        // Map API response to OutletOption interface
        const mappedOutlets: OutletOption[] = (response.getOutletList || []).map(
          (outlet) => ({
            id: String(outlet.id),
            name: outlet.name,
          })
        );
        setOutlets(mappedOutlets);
      }
    } catch (error: any) {
      console.error("Error fetching outlets:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch outlets",
        variant: "destructive",
      });
    }
  };

  const form = useForm<FraudRuleFormValues>({
    resolver: zodResolver(fraudRuleFormSchema),
    defaultValues: {
      ruleName: "",
      outletId: "",
      transactionCount: "",
      days: "",
      invoiceAmountThreshold: "",
      invoiceAction: "",
      redeemAmountThreshold: "",
      redeemAction: "",
    },
  });

  const onSubmit = async (data: FraudRuleFormValues) => {
    try {
      // TODO: Replace with actual API call
      console.log("Fraud Rule Form Data:", data);
      
      toast({
        title: "Success",
        description: "Fraud rule added successfully",
      });

      form.reset();
      setIsAddModalOpen(false);
      
      // TODO: Refresh fraud rules list after adding
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to add fraud rule",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    setIsAddModalOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Real-Time Fraud Detection</h1>
          <p className="text-gray-600">AI-powered fraud detection and prevention system</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Activity className="h-3 w-3 mr-1" />
            System Active
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Alerts</p>
                <p className="text-2xl font-bold text-red-600">{activeAlerts}</p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Risk Alerts</p>
                <p className="text-2xl font-bold text-orange-600">{highRiskAlerts}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Blocked Amount</p>
                <p className="text-2xl font-bold text-blue-600">$12.5K</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Detection Rate</p>
                <p className="text-2xl font-bold text-green-600">94.2%</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Fraud Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Recent Fraud Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Alert ID</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Risk Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fraudAlerts.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell className="font-medium">{alert.id}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{alert.memberName}</div>
                        <div className="text-sm text-gray-500">{alert.memberId}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getSeverityColor(alert.alertType)}>
                        {alert.alertType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              alert.riskScore >= 80 ? 'bg-red-500' : 
                              alert.riskScore >= 60 ? 'bg-orange-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${alert.riskScore}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{alert.riskScore}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusColor(alert.status)}>
                        {alert.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{alert.timestamp}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAlertAction(alert.id, 'View')}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        {alert.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAlertAction(alert.id, 'Block')}
                          >
                            <Ban className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add Fraud Rule Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Fraud Rule</DialogTitle>
            <DialogDescription>
              Create a new fraud detection rule
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Rule Name and Outlets in the same line */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="ruleName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rule Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter rule name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="outletId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Outlets *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select outlet" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {outlets.map((outlet) => (
                            <SelectItem key={outlet.id} value={outlet.id}>
                              {outlet.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Tabs Structure */}
              <Tabs defaultValue="transaction-count" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="transaction-count">
                    Transaction Count
                  </TabsTrigger>
                  <TabsTrigger value="invoice-amount">
                    Invoice Amount
                  </TabsTrigger>
                  <TabsTrigger value="redemption-amount">
                    Redemption
                  </TabsTrigger>
                </TabsList>

                {/* Transaction Count Tab */}
                <TabsContent value="transaction-count" className="space-y-4 mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-[2fr,auto,2fr] items-end gap-3">
                    <FormField
                      control={form.control}
                      name="transactionCount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>On reaching no of transaction *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Enter number of transactions"
                              min="1"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex items-center justify-center h-full text-sm text-gray-600">
                      in
                    </div>

                    <FormField
                      control={form.control}
                      name="days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Days *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Enter number of days"
                              min="1"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </TabsContent>

                {/* Invoice Amount Tab */}
                <TabsContent value="invoice-amount" className="space-y-4 mt-6">
                  <div className="space-y-2">
                    <FormLabel>On Single Invoice amount exceeding *</FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-[2fr,2fr] gap-4">
                      <FormField
                        control={form.control}
                        name="invoiceAmountThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Enter invoice amount"
                                min="1"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="invoiceAction"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select action" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="block-earn-burn">
                                    Block earn and burn transactions
                                  </SelectItem>
                                  <SelectItem value="block-all-future">
                                    Block all future transactions
                                  </SelectItem>
                                  <SelectItem value="no-action">
                                    No action
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Redemption Tab */}
                <TabsContent value="redemption-amount" className="space-y-4 mt-6">
                  <div className="space-y-2">
                    <FormLabel>
                      If points redeem on single bill exceeds *
                    </FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-[2fr,2fr] gap-4">
                      <FormField
                        control={form.control}
                        name="redeemAmountThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Enter redemption amount"
                                min="1"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="redeemAction"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select action" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="block-earn-burn">
                                    Block earn and burn transactions
                                  </SelectItem>
                                  <SelectItem value="block-all-future">
                                    Block all future transactions
                                  </SelectItem>
                                  <SelectItem value="no-action">
                                    No action
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Add Rule
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FraudDetection;
